import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';
import Container from 'common/Container';
import { mediaKitLandingPath } from '../../../data/staticLandingSlugs';

/** Marca DHL: rojo y amarillo por defecto en propuestas que usan `content.ui.brand` vacío con `preset: 'dhl'`. */
export const DHL_BRAND_PRESET = {
  primary: '#D40511',
  accent: '#FFCC00',
  soft: 'rgba(212, 5, 17, 0.08)',
  ctaSecondary: '#FFCC00',
  ctaSecondaryText: '#1a1a1a',
  heroGradient:
    'linear-gradient(140deg, #141414 0%, #3d080c 38%, #D40511 72%, #9a0310 100%)',
};

const DEFAULT_BRAND = {
  ...DHL_BRAND_PRESET,
};

export function mergeBrand(ui = {}) {
  const base = ui.preset === 'tno' ? {
    primary: '#1565c0',
    accent: '#00acc1',
    soft: 'rgba(21, 101, 192, 0.08)',
    ctaSecondary: '#c9a227',
    ctaSecondaryText: '#1a237e',
    heroGradient:
      'linear-gradient(130deg, rgba(6,11,33,0.97) 0%, rgba(21,32,83,0.96) 45%, rgba(0,187,255,0.55) 100%)',
  } : DEFAULT_BRAND;
  return { ...base, ...(ui.brand || {}) };
}

export function SectionHeader({ overline, title, description, brand, id }) {
  const primary = brand?.primary || DEFAULT_BRAND.primary;
  return (
    <Box id={id} component="header" mb={{ xs: 3, md: 4 }}>
      {overline && (
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.12em',
            color: primary,
            display: 'block',
            mb: 1,
          }}
        >
          {overline}
        </Typography>
      )}
      {title && (
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.65rem', md: '2.1rem' },
            lineHeight: 1.2,
            mb: description ? 1.5 : 0,
          }}
        >
          {title}
        </Typography>
      )}
      {description && (
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ lineHeight: 1.85, maxWidth: 720, fontSize: { xs: '1rem', md: '1.0625rem' } }}
        >
          {description}
        </Typography>
      )}
      <Box mt={2} width={48} height={4} borderRadius={2} sx={{ bgcolor: primary }} />
    </Box>
  );
}

SectionHeader.propTypes = {
  overline: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  brand: PropTypes.object,
  id: PropTypes.string,
};

const PILLAR_ACCENTS = ['#D40511', '#1565c0', '#00695c', '#5e35b1', '#c62828', '#00838f', '#e65100'];

export function ProgramCard({ item, index, brand, IconComp }) {
  const accent = item.accent || PILLAR_ACCENTS[index % PILLAR_ACCENTS.length];
  const soft = `${accent}12`;
  const num = String(index + 1).padStart(2, '0');

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        overflow: 'hidden',
        transition: 'transform 0.22s ease, box-shadow 0.22s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2.5, md: 3 },
          py: 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          bgcolor: soft,
          borderBottom: '1px solid',
          borderColor: 'rgba(0,0,0,0.06)',
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 2,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: accent,
            color: 'common.white',
            boxShadow: `0 4px 14px ${accent}55`,
          }}
        >
          {IconComp ? <IconComp /> : null}
        </Box>
        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
            <Typography
              component="span"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 800,
                letterSpacing: '0.08em',
                color: accent,
                opacity: 0.9,
              }}
            >
              {num}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25, m: 0 }}>
              {item.title}
            </Typography>
          </Box>
          {item.badge && (
            <Chip
              label={item.badge}
              size="small"
              sx={{
                height: 26,
                fontWeight: 600,
                fontSize: '0.75rem',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'rgba(0,0,0,0.08)',
              }}
            />
          )}
        </Box>
      </Box>
      <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
        {item.summary && (
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.75,
              color: 'text.primary',
              fontSize: '0.95rem',
              mb: item.topics?.length ? 2 : 0,
            }}
          >
            {item.summary}
          </Typography>
        )}
        {Array.isArray(item.topics) && item.topics.length > 0 && (
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {item.topics.map((t, j) => (
              <Box
                component="li"
                key={j}
                sx={{
                  display: 'flex',
                  gap: 1.25,
                  alignItems: 'flex-start',
                  py: 0.75,
                  borderTop: j > 0 ? '1px solid' : 'none',
                  borderColor: 'rgba(0,0,0,0.06)',
                }}
              >
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 20, color: accent, mt: 0.15, flexShrink: 0 }}
                />
                <Typography variant="body2" sx={{ lineHeight: 1.65, color: 'text.secondary' }}>
                  {t}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {item.mediaKitSlug && (
          <Box
            component={RouterLink}
            to={mediaKitLandingPath(item.mediaKitSlug)}
            sx={{
              mt: 2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              fontWeight: 700,
              fontSize: '0.875rem',
              color: accent,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {item.mediaKitLinkLabel || 'Ver media kit del programa'}
            <ArrowForwardIcon sx={{ fontSize: 18 }} />
          </Box>
        )}
      </Box>
    </Card>
  );
}

ProgramCard.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  brand: PropTypes.object,
  IconComp: PropTypes.elementType,
};

export function ProgramIndexNav({ items, brand, sectionId }) {
  const primary = brand?.primary || DEFAULT_BRAND.primary;
  if (!items?.length) return null;

  return (
    <Box
      sx={{
        display: { xs: 'none', lg: 'block' },
        position: 'sticky',
        top: 88,
        mb: 3,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 1 }}>
        EN ESTA PÁGINA
      </Typography>
      <Box component="nav" mt={1.5} display="flex" flexDirection="column" gap={0.5}>
        {items.map((item, i) => {
          const anchor = item.id ? `${sectionId}-${item.id}` : `${sectionId}-${i}`;
          return (
            <Box
              key={item.id || i}
              component="a"
              href={`#${anchor}`}
              sx={{
                py: 0.75,
                px: 1.5,
                borderRadius: 1.5,
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: 'text.secondary',
                textDecoration: 'none',
                borderLeft: '3px solid transparent',
                transition: 'all 0.15s',
                '&:hover': {
                  color: primary,
                  bgcolor: `${primary}0a`,
                  borderLeftColor: primary,
                },
              }}
            >
              {item.title}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

ProgramIndexNav.propTypes = {
  items: PropTypes.array,
  brand: PropTypes.object,
  sectionId: PropTypes.string,
};

export function PlanHighlightStrip({ strip, brand, totalValue }) {
  const primary = brand?.primary || DEFAULT_BRAND.primary;
  const accent = brand?.accent || DEFAULT_BRAND.accent;
  if (!strip?.title && !strip?.description) return null;

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 55%, ${accent}99 100%)`,
        color: 'common.white',
      }}
    >
      <Container>
        <Box
          py={{ xs: 4, md: 5 }}
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
          gap={3}
        >
          <Box flex={1} maxWidth={640}>
            {strip.title && (
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                {strip.title}
              </Typography>
            )}
            {strip.description && (
              <Typography variant="body1" sx={{ lineHeight: 1.8, opacity: 0.95 }}>
                {strip.description}
              </Typography>
            )}
          </Box>
          {totalValue && (
            <Box
              sx={{
                px: 3,
                py: 2,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(8px)',
                textAlign: { xs: 'left', md: 'center' },
                minWidth: { md: 180 },
              }}
            >
              <Typography variant="overline" sx={{ opacity: 0.85, fontWeight: 600 }}>
                Total mensual
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                {totalValue}
              </Typography>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}

PlanHighlightStrip.propTypes = {
  strip: PropTypes.object,
  brand: PropTypes.object,
  totalValue: PropTypes.string,
};

export function ValuePillarCard({ item, index, brand }) {
  const primary = brand?.primary || DEFAULT_BRAND.primary;
  return (
    <Card
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        bgcolor: index % 2 === 0 ? 'background.paper' : `${primary}06`,
      }}
    >
      <Box
        width={32}
        height={32}
        borderRadius="50%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        mb={1.5}
        sx={{ bgcolor: `${primary}18`, color: primary, fontWeight: 800, fontSize: '0.875rem' }}
      >
        {index + 1}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        {item.title}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.75 }}>
        {item.description}
      </Typography>
    </Card>
  );
}

ValuePillarCard.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  brand: PropTypes.object,
};

export function ExtraGroupCard({ group, brand, index }) {
  const primary = brand?.primary || DEFAULT_BRAND.primary;
  const accents = [primary, '#00695c', '#5e35b1', '#c62828'];
  const accent = accents[index % accents.length];

  return (
    <Card
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        borderTop: `4px solid ${accent}`,
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, lineHeight: 1.35 }}>
        {group.label}
      </Typography>
      {Array.isArray(group.bullets) && (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {group.bullets.map((b, bi) => (
            <Box
              component="li"
              key={bi}
              sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', py: 0.85 }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: accent, mt: 0.2, flexShrink: 0 }} />
              <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.65 }}>
                {b}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {group.mediaKitSlug && (
        <Box
          component={RouterLink}
          to={mediaKitLandingPath(group.mediaKitSlug)}
          sx={{
            mt: 2,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontWeight: 700,
            fontSize: '0.875rem',
            color: accent,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {group.mediaKitLinkLabel || 'Ver media kit del programa'}
          <ArrowForwardIcon sx={{ fontSize: 18 }} />
        </Box>
      )}
    </Card>
  );
}

ExtraGroupCard.propTypes = {
  group: PropTypes.object.isRequired,
  brand: PropTypes.object,
  index: PropTypes.number,
};

export function CtaBand({ areYouReady, brand, whatsappNumber }) {
  const primary = brand?.primary || DEFAULT_BRAND.primary;
  if (!areYouReady?.title && !areYouReady?.description) return null;

  return (
    <Box
      sx={{
        py: { xs: 5, md: 7 },
        background: `linear-gradient(160deg, ${primary}14 0%, ${primary}08 50%, transparent 100%)`,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container>
        <Box textAlign="center" maxWidth={560} mx="auto">
          {areYouReady.title && (
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
              {areYouReady.title}
            </Typography>
          )}
          {areYouReady.description && (
            <Typography variant="body1" color="textSecondary" sx={{ lineHeight: 1.85, mb: 3 }}>
              {areYouReady.description}
            </Typography>
          )}
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="center" gap={2}>
            {areYouReady.ctaText && (
              <Box
                component="a"
                href={`https://wa.me/${whatsappNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 700,
                  textDecoration: 'none',
                  bgcolor: '#25D366',
                  color: '#fff',
                  '&:hover': { bgcolor: '#1fb855' },
                }}
              >
                {areYouReady.ctaText}
              </Box>
            )}
            {areYouReady.contactEmail && (
              <Box
                component="a"
                href={`mailto:${areYouReady.contactEmail}`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 700,
                  textDecoration: 'none',
                  border: '2px solid',
                  borderColor: primary,
                  color: primary,
                  '&:hover': { bgcolor: `${primary}0a` },
                }}
              >
                {areYouReady.contactEmail}
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

CtaBand.propTypes = {
  areYouReady: PropTypes.object,
  brand: PropTypes.object,
  whatsappNumber: PropTypes.string,
};
